import type { DrawingDocument } from '../model';

/**
 * #615: which stored atom and bond each drawn one is (submitted ID -> stored ID), as the server
 * reports when saving reused an identical Molecule whose atoms are numbered differently.
 */
export interface StructureCorrespondence {
  readonly atoms: Readonly<Record<string, string>>;
  readonly bonds: Readonly<Record<string, string>>;
}

/** The structure in `document` and the Molecule it was matched to don't line up atom for atom. */
export class StructureCorrespondenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StructureCorrespondenceError';
  }
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * #615: renames every atom and bond reference in `document` onto the stored Molecule's IDs, so the
 * depiction, stereo and annotations saved with a reused Molecule address *its* graph. The picture
 * and the chemistry are unchanged: every reference is renamed consistently, which is also why any
 * of a symmetric molecule's equivalent matchings is correct.
 *
 * Field by field, from the drawing schema -- the structure (graph, atoms, bonds incl. a dative
 * bond's donor, charges, tetrahedral and double-bond stereo), the depiction (coordinates, depths,
 * labels, bond styles incl. a wedge's narrow end and a fused double bond's facing side, bond length
 * overrides, appearance), annotations (`atomId`/`atomIds`/`bondId`/`bondIds` -- the only atom and
 * bond references annotations carry; free text is never touched) and derived property sets
 * (aromaticity, CIP, partial charges). Throws {@link StructureCorrespondenceError} rather than
 * keep a reference the Molecule can't resolve -- except a hydrogen drawn as an atom where the
 * Molecule has an implicit one (#951): it and its bond stay on the drawing under their own IDs
 * (fresh ones if the Molecule already uses them), as {@link drawnHydrogenIds} finds them.
 */
export function remapStructureIds(document: DrawingDocument, correspondence: StructureCorrespondence): DrawingDocument {
  const kept = keptHydrogens(document, correspondence);
  const atom = (id: string): string => {
    const mapped = correspondence.atoms[id] ?? kept.atoms.get(id);
    if (mapped === undefined) {
      throw new StructureCorrespondenceError(`The saved molecule has no atom matching ${id}.`);
    }
    return mapped;
  };
  const bond = (id: string): string => {
    const mapped = correspondence.bonds[id] ?? kept.bonds.get(id);
    if (mapped === undefined) {
      throw new StructureCorrespondenceError(`The saved molecule has no bond matching ${id}.`);
    }
    return mapped;
  };
  const atomKeyed = <V>(record: Readonly<Record<string, V>> | undefined): Record<string, V> | undefined =>
    record === undefined
      ? undefined
      : Object.fromEntries(Object.entries(record).map(([id, value]) => [atom(id), value]));
  const bondKeyed = <V>(record: Readonly<Record<string, V>> | undefined): Record<string, V> | undefined =>
    record === undefined
      ? undefined
      : Object.fromEntries(Object.entries(record).map(([id, value]) => [bond(id), value]));

  const { structure, depiction } = document;
  for (const node of structure.graph.nodes) atom(node.id);
  for (const edge of structure.graph.edges) bond(edge.id);

  const remappedStructure: DrawingDocument['structure'] = {
    ...structure,
    graph: {
      ...structure.graph,
      nodes: structure.graph.nodes.map((node) => ({ ...node, id: atom(node.id) })),
      edges: structure.graph.edges.map((edge) => ({
        ...edge,
        id: bond(edge.id),
        source: atom(edge.source),
        target: atom(edge.target)
      }))
    },
    atoms: atomKeyed(structure.atoms)!,
    bonds: Object.fromEntries(
      Object.entries(structure.bonds).map(([id, properties]) => [
        bond(id),
        properties.donorAtomId !== undefined
          ? { ...properties, donorAtomId: atom(properties.donorAtomId) }
          : properties.ligandAtomIds !== undefined
            ? { ...properties, ligandAtomIds: properties.ligandAtomIds.map(atom) as [string, string, ...string[]] }
            : properties
      ])
    ),
    formalCharges: atomKeyed(structure.formalCharges)!,
    stereochemistry: {
      tetrahedral: Object.fromEntries(
        Object.entries(structure.stereochemistry.tetrahedral).map(([id, stereo]) => [
          atom(id),
          {
            ...stereo,
            ligands: stereo.ligands.map((ligand) =>
              'atomId' in ligand ? { ...ligand, atomId: atom(ligand.atomId) } : ligand
            ) as unknown as typeof stereo.ligands
          }
        ])
      ),
      doubleBonds: Object.fromEntries(
        Object.entries(structure.stereochemistry.doubleBonds).map(([id, stereo]) => [
          bond(id),
          {
            ...stereo,
            referenceAtomIds: [atom(stereo.referenceAtomIds[0]), atom(stereo.referenceAtomIds[1])] as const
          }
        ])
      )
    }
  };

  const coordinateSet: Mutable<DrawingDocument['depiction']['coordinateSet']> = {
    ...depiction.coordinateSet,
    coordinates: atomKeyed(depiction.coordinateSet.coordinates)!
  };
  if (depiction.coordinateSet.depths) coordinateSet.depths = atomKeyed(depiction.coordinateSet.depths)!;
  const remappedDepiction: Mutable<DrawingDocument['depiction']> = {
    ...depiction,
    coordinateSet,
    atomLabels: atomKeyed(depiction.atomLabels)!,
    bondStyles: Object.fromEntries(
      Object.entries(depiction.bondStyles).map(([id, style]) => {
        if ('fromAtomId' in style) return [bond(id), { ...style, fromAtomId: atom(style.fromAtomId) }];
        if ('facingAtomId' in style) return [bond(id), { ...style, facingAtomId: atom(style.facingAtomId) }];
        return [bond(id), style];
      })
    ),
    bondLengthOverrides: bondKeyed(depiction.bondLengthOverrides)!
  };
  if (depiction.cycleFills)
    remappedDepiction.cycleFills = depiction.cycleFills.map((fill) => ({
      ...fill,
      atomIds: fill.atomIds.map(atom) as [string, string, string, ...string[]]
    }));
  if (depiction.atomAppearance) remappedDepiction.atomAppearance = atomKeyed(depiction.atomAppearance)!;
  // #736 step 2 -- a contracted group names its attachment and its atoms.
  if (depiction.contractedGroups)
    remappedDepiction.contractedGroups = Object.fromEntries(
      Object.entries(depiction.contractedGroups).map(([attachmentId, entry]) => [
        atom(attachmentId),
        { ...entry, atomIds: entry.atomIds.map(atom) as unknown as typeof entry.atomIds }
      ])
    );
  if (depiction.bondAppearance) remappedDepiction.bondAppearance = bondKeyed(depiction.bondAppearance)!;

  return {
    ...document,
    structure: remappedStructure,
    depiction: remappedDepiction,
    annotations: document.annotations.map((annotation) => remapReferences(annotation, atom, bond)),
    derivedPropertySets: document.derivedPropertySets.map((set) => {
      switch (set.kind) {
        case 'aromaticity':
          return { ...set, atomIds: set.atomIds.map(atom), bondIds: set.bondIds.map(bond) };
        case 'cip':
          return {
            ...set,
            atomDescriptors: atomKeyed(set.atomDescriptors)!,
            bondDescriptors: bondKeyed(set.bondDescriptors)!
          };
        case 'partial-charge':
          return { ...set, charges: atomKeyed(set.charges)! };
        default:
          return set;
      }
    }) as DrawingDocument['derivedPropertySets']
  };
}

/**
 * #951: the hydrogens drawn as atoms that the matched Molecule doesn't have (it holds them as
 * implicit hydrogens), each with its one bond -- old ID -> the ID it keeps on the drawing. Only a
 * hydrogen with a single bond to a matched atom qualifies; anything else unmatched still throws.
 */
function keptHydrogens(
  document: DrawingDocument,
  correspondence: StructureCorrespondence
): { atoms: Map<string, string>; bonds: Map<string, string> } {
  const atoms = new Map<string, string>();
  const bonds = new Map<string, string>();
  const takenAtoms = new Set(Object.values(correspondence.atoms));
  const takenBonds = new Set(Object.values(correspondence.bonds));
  const fresh = (id: string, taken: Set<string>) => {
    let candidate = id;
    for (let suffix = 1; taken.has(candidate); suffix++) candidate = `${id}-h${suffix}`;
    taken.add(candidate);
    return candidate;
  };
  for (const node of document.structure.graph.nodes) {
    if (node.id in correspondence.atoms || document.structure.atoms[node.id]?.atomicNumber !== 1) continue;
    const edges = document.structure.graph.edges.filter((edge) => edge.source === node.id || edge.target === node.id);
    const [edge] = edges;
    if (edges.length !== 1 || !edge || edge.id in correspondence.bonds) continue;
    const neighbour = edge.source === node.id ? edge.target : edge.source;
    if (!(neighbour in correspondence.atoms)) continue;
    atoms.set(node.id, fresh(node.id, takenAtoms));
    bonds.set(edge.id, fresh(edge.id, takenBonds));
  }
  return { atoms, bonds };
}

/**
 * #951: after {@link remapStructureIds}, the drawing's hydrogens that the Molecule doesn't have --
 * each with its neighbour and bond, as the drawing stores them (`drawnHydrogens`).
 */
export function drawnHydrogenIds(
  remapped: DrawingDocument,
  correspondence: StructureCorrespondence
): Record<string, { atomId: string; bondId: string }> {
  const moleculeAtoms = new Set(Object.values(correspondence.atoms));
  const result: Record<string, { atomId: string; bondId: string }> = {};
  for (const node of remapped.structure.graph.nodes) {
    if (moleculeAtoms.has(node.id)) continue;
    const edge = remapped.structure.graph.edges.find(
      (candidate) => candidate.source === node.id || candidate.target === node.id
    );
    if (!edge) continue;
    result[node.id] = { atomId: edge.source === node.id ? edge.target : edge.source, bondId: edge.id };
  }
  return result;
}

/** Renames `atomId`/`atomIds`/`bondId`/`bondIds` anywhere inside an annotation; nothing else. */
function remapReferences<T>(value: T, atom: (id: string) => string, bond: (id: string) => string): T {
  if (Array.isArray(value)) return value.map((item) => remapReferences(item, atom, bond)) as T;
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => {
      if (key === 'atomId' && typeof item === 'string') return [key, atom(item)];
      if (key === 'bondId' && typeof item === 'string') return [key, bond(item)];
      if (key === 'atomIds' && Array.isArray(item)) return [key, item.map((id: string) => atom(id))];
      if (key === 'bondIds' && Array.isArray(item)) return [key, item.map((id: string) => bond(id))];
      return [key, remapReferences(item, atom, bond)];
    })
  ) as T;
}
